import { z } from 'zod';
import { FlowNodeSchema } from './nodes.js';
import { FlowEdgeSchema } from './edges.js';
import { FlowInputFieldSchema } from './input.js';

// --- Legacy trigger types ---
//
// DEPRECATED: triggers are graph nodes now (`type: 'trigger'` in
// FlowNodeSchema). These schemas remain only so legacy rows and
// flow_versions snapshots still parse; `upgradeFlowGraph` converts them
// into trigger (+ filter) nodes on read. Do not add new variants.

// All variant schemas use `.strict()` so writing cross-variant fields (e.g.
// `channelId` on a `manual` trigger) errors at the validation boundary
// instead of being silently stripped by the discriminated union — silent
// stripping was leaving flows persisted as `{ type: 'manual' }` with the
// user's slack filters lost. Existing rows are not re-validated on read,
// so this only affects future writes.
export const FlowLinearIssueTriggerSchema = z
  .object({
    type: z.literal('linear_issue'),
    model: z.string().optional(),
    connectionId: z.string().optional(),
    teamId: z.string().optional(),
    labelTrigger: z.string().optional(),
  })
  .strict();

export const GITHUB_PR_ACTIONS = [
  'opened',
  'reopened',
  'closed',
  'synchronize',
  'edited',
  'ready_for_review',
  'converted_to_draft',
  'review_requested',
  'review_request_removed',
  'labeled',
  'unlabeled',
  'assigned',
  'unassigned',
] as const;

export const FlowGithubPRActionSchema = z.enum(GITHUB_PR_ACTIONS);
export type FlowGithubPRAction = z.infer<typeof FlowGithubPRActionSchema>;

export const FlowGithubPRTriggerSchema = z
  .object({
    type: z.literal('github_pr'),
    model: z.string().optional(),
    repoSlug: z.string().optional(),
    baseBranch: z.string().optional(),
    /**
     * GitHub PR webhook actions that fire this trigger. When omitted the
     * trigger only fires on `opened`, preserving the historical default.
     */
    actions: z.array(FlowGithubPRActionSchema).nonempty().optional(),
    /**
     * Restrict the trigger to PRs where one of these GitHub logins has been
     * requested as a reviewer. Combine with `actions: ['review_requested']`
     * to build a "review my PRs" bot.
     */
    reviewerLogins: z.array(z.string().min(1)).nonempty().optional(),
  })
  .strict();

export const GITHUB_ISSUE_ACTIONS = [
  'opened',
  'edited',
  'closed',
  'reopened',
  'labeled',
  'unlabeled',
  'assigned',
  'unassigned',
] as const;

export const FlowGithubIssueActionSchema = z.enum(GITHUB_ISSUE_ACTIONS);
export type FlowGithubIssueAction = z.infer<typeof FlowGithubIssueActionSchema>;

export const FlowGithubIssueTriggerSchema = z
  .object({
    type: z.literal('github_issue'),
    model: z.string().optional(),
    repoSlug: z.string().optional(),
    /**
     * GitHub issue webhook actions that fire this trigger. When omitted the
     * trigger only fires on `opened`, mirroring the `github_pr` default.
     */
    actions: z.array(FlowGithubIssueActionSchema).nonempty().optional(),
    /**
     * Restrict the trigger to issues carrying one of these labels. Combine
     * with `actions: ['labeled']` to build a "triage labeled issues" bot.
     */
    labels: z.array(z.string().min(1)).nonempty().optional(),
    /**
     * Restrict the trigger to issues assigned to one of these GitHub logins.
     * Combine with `actions: ['assigned']` for a "work my issues" bot.
     */
    assigneeLogins: z.array(z.string().min(1)).nonempty().optional(),
  })
  .strict();

export const FlowManualTriggerSchema = z
  .object({
    type: z.literal('manual'),
    model: z.string().optional(),
  })
  .strict();

export const FlowCronTriggerSchema = z
  .object({
    type: z.literal('cron'),
    model: z.string().optional(),
    schedule: z.string(),
    timezone: z.string().optional(),
  })
  .strict();

export const FlowSentryIssueTriggerSchema = z
  .object({
    type: z.literal('sentry_issue'),
    model: z.string().optional(),
    sentryProjectSlug: z.string().optional(),
  })
  .strict();

/**
 * Fires when a Pylon issue webhook (`event: 'issue.created'`) arrives.
 * `connectionId` optionally narrows the trigger to a specific Pylon
 * connection — useful when an installation has multiple Pylon
 * workspaces wired up.
 */
export const FlowPylonIssueTriggerSchema = z
  .object({
    type: z.literal('pylon_issue'),
    model: z.string().optional(),
    connectionId: z.string().optional(),
  })
  .strict();

/**
 * Fires when a Slack message event arrives. All filter fields are optional
 * — an empty filter matches every message.
 *
 * Matching uses stable Slack IDs (`*Id` fields), resolved via lookup tools
 * at flow-edit time. The companion `*Name` fields are **display-only**:
 * they're stored so the UI can render a human-readable label without an
 * extra Slack API round-trip, but the orchestrator's `findFlowsByTrigger`
 * never reads them. A `*Name` without its `*Id` would be a silently
 * non-functional filter, so the `superRefine` below rejects that shape
 * at parse time.
 */
export const FlowSlackMessageTriggerSchema = z
  .object({
    type: z.literal('slack_message'),
    model: z.string().optional(),
    teamId: z.string().optional(),
    /** Display label for `teamId`. Not used for matching. */
    teamName: z.string().optional(),
    channelId: z.string().optional(),
    /** Display label for `channelId`. Not used for matching. */
    channelName: z.string().optional(),
    userId: z.string().optional(),
    /** Display label for `userId`. Not used for matching. */
    userName: z.string().optional(),
    botId: z.string().optional(),
    /** Display label for `botId`. Not used for matching. */
    botName: z.string().optional(),
    /** Case-insensitive substring match on the message text. */
    textIncludes: z.string().optional(),
    /** When true, only thread replies trigger (not top-level messages). */
    threadOnly: z.boolean().optional(),
  })
  .strict()
  .superRefine((value, ctx) => {
    // Display labels are useless (and misleading) without their stable
    // ID. Reject the shape so the flow editor agent has to resolve the
    // name via `resolve_slack_channel` / `resolve_slack_user` /
    // `resolve_slack_app_or_bot` before saving the trigger.
    const pairs: Array<
      [nameField: 'teamName' | 'channelName' | 'userName' | 'botName', idField: string]
    > = [
      ['teamName', 'teamId'],
      ['channelName', 'channelId'],
      ['userName', 'userId'],
      ['botName', 'botId'],
    ];
    for (const [nameField, idField] of pairs) {
      if (value[nameField] && !value[idField as keyof typeof value]) {
        ctx.addIssue({
          code: 'custom',
          path: [idField],
          message: `${idField} is required when ${nameField} is set. Resolve the name to a stable Slack ID via the lookup tools before saving.`,
        });
      }
    }
  });

/** @deprecated Triggers are graph nodes now — see FlowTriggerNodeSchema. */
export const FlowTriggerSchema = z.discriminatedUnion('type', [
  FlowLinearIssueTriggerSchema,
  FlowGithubPRTriggerSchema,
  FlowGithubIssueTriggerSchema,
  FlowManualTriggerSchema,
  FlowCronTriggerSchema,
  FlowSentryIssueTriggerSchema,
  FlowPylonIssueTriggerSchema,
  FlowSlackMessageTriggerSchema,
]);
/** @deprecated Triggers are graph nodes now — see FlowTriggerNodeConfig. */
export type FlowTrigger = z.infer<typeof FlowTriggerSchema>;

// --- Input schema ---

// Re-exported from input.ts (moved so nodes.ts can embed input fields in
// manual trigger configs without an import cycle).
export {
  FlowInputFieldSchema,
  FlowInputFieldTypeSchema,
  type FlowInputField,
  type FlowInputFieldType,
} from './input.js';

// --- Flow definition ---

export const FlowGraphSchema = z.object({
  nodes: z.array(FlowNodeSchema).min(1),
  edges: z.array(FlowEdgeSchema),
});
export type FlowGraph = z.infer<typeof FlowGraphSchema>;

/**
 * Display name for a flow. Bounded to match the agent name limit
 * (`UpdateAgentRequestSchema.name`, `shared/api.types.ts`). The single source
 * of the name constraints; `.optional()` is applied per use-site.
 */
const FlowNameSchema = z.string().min(1).max(200);

export const FlowDefinitionSchema = z.object({
  id: z.string().min(1),
  name: FlowNameSchema,
  description: z.string().optional(),
  /**
   * @deprecated Triggers live in `nodes` as `type: 'trigger'` entries.
   * Read paths upgrade legacy rows via `upgradeFlowGraph`, so consumers
   * should never see a populated value here; kept only so unupgraded
   * snapshots/portables still parse during the transition.
   */
  trigger: FlowTriggerSchema.optional(),
  /** @deprecated Manual trigger nodes carry their own `fields` now. */
  inputSchema: z.array(FlowInputFieldSchema).optional(),
  nodes: z.array(FlowNodeSchema).min(1),
  edges: z.array(FlowEdgeSchema),
  enabled: z.boolean(),
  system: z.boolean().optional(),
  /**
   * Owning organization id. Always set after the non-nullable-org
   * rollout (system flows live under `'system' sentinel`). Carried on the
   * entity so cron/webhook trigger fan-out can re-stamp the org on
   * runs without a separate DB lookup.
   */
  organizationId: z.string(),
  /**
   * Per-resource owner. A single prefixed id whose kind (`user` /
   * `team` / `organization` / `project` / …) is derived from the
   * prefix. `null` is the host-tier sentinel used by legacy rows
   * that pre-date the resource-ownership feature (visible to all
   * org members). Backfilled org-owned rows carry the row's
   * `organizationId` as the explicit org sentinel.
   *
   * Carried on the entity schema so the web cards can render an
   * `OwnerChip` without an extra round-trip; the orchestrator sets it
   * from the underlying row.
   */
  ownerId: z.string().nullable().optional(),
  /** ID of the currently-published version snapshot, if any. */
  publishedVersionId: z.string().nullable().optional(),
  /** Epoch-ms when the flow was last published. */
  publishedAt: z.number().nullable().optional(),
  /**
   * Epoch-ms when `enabled` last flipped ON. Stamped by the toggle path
   * (and at create for flows born enabled), NOT by content edits, so cron
   * catch-up can bound published schedules at `max(publishedAt, enabledAt)`
   * and never replay occurrences from a disabled window. `null` means the
   * flow has not been toggled since the column existed — treated as
   * "enabled no later than publish" (no behavior change for such rows).
   */
  enabledAt: z.number().nullable().optional(),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type FlowDefinition = z.infer<typeof FlowDefinitionSchema>;

/**
 * Resource-ownership block accepted on flow create requests. Mirrors
 * the shared `ResourceOwnerInputSchema`. Optional: when omitted the
 * create route defaults ownership to the calling user, and an update
 * leaves the current owner unchanged. Routes validate via
 * `assertCallerCanOwn` that the caller is allowed to claim the
 * requested owner.
 */
const FlowOwnerInputSchema = z.object({
  ownerId: z.string().min(1).nullable(),
});

/**
 * Name assigned to a flow created without one. Shared so the server default and
 * the web editor's placeholder reference a single source and can't drift.
 */
export const DEFAULT_FLOW_NAME = 'Untitled automation';

/**
 * Name the web editor assigns to the blank graph it creates for the
 * "Describe Your Automation" input, before the AI builds the real thing.
 */
export const SCAFFOLD_FLOW_NAME = 'New Automation';

/**
 * Every name that has meant "this is an unbuilt scaffold". Shared so the
 * editor that writes the name and the chat route that detects it can't
 * drift. `New Flow` is the pre-rename default and stays recognised: drafts
 * created before the Automations rename still carry it, and dropping it
 * would silently cost them the build-from-scratch system prompt.
 */
export const SCAFFOLD_FLOW_NAMES: ReadonlySet<string> = new Set([SCAFFOLD_FLOW_NAME, 'New Flow']);

export const CreateFlowDefinitionSchema = z.object({
  // Optional: when omitted the server assigns `DEFAULT_FLOW_NAME`. An explicit
  // empty string is still rejected; clients omit the field rather than send `''`.
  name: FlowNameSchema.optional(),
  description: z.string().optional(),
  /**
   * @deprecated Transition-window escape hatch: when provided alongside a
   * node list without trigger nodes, the server normalizes-on-write via
   * `upgradeFlowGraph`. New clients author trigger nodes directly.
   */
  trigger: FlowTriggerSchema.optional(),
  /** @deprecated See `trigger`. */
  inputSchema: z.array(FlowInputFieldSchema).optional(),
  nodes: z.array(FlowNodeSchema).min(1),
  edges: z.array(FlowEdgeSchema),
  owner: FlowOwnerInputSchema.optional(),
});
export type CreateFlowDefinition = z.infer<typeof CreateFlowDefinitionSchema>;

export const UpdateFlowDefinitionSchema = z.object({
  name: FlowNameSchema.optional(),
  description: z.string().optional(),
  trigger: FlowTriggerSchema.optional(),
  inputSchema: z.array(FlowInputFieldSchema).optional(),
  nodes: z.array(FlowNodeSchema).min(1).optional(),
  edges: z.array(FlowEdgeSchema).optional(),
  /**
   * Resource ownership transfer. When provided, the caller must already
   * be allowed to claim the requested owner. Routes enforce this via
   * `assertCallerCanOwn`.
   */
  owner: FlowOwnerInputSchema.optional(),
});
export type UpdateFlowDefinition = z.infer<typeof UpdateFlowDefinitionSchema>;
